import { Toaster } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import PCInOutPermitDetailsCard from "../../../components/card/PCInOutPermitDetailsCard";
import {
  createRef,
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  PCInOutPermitRequestDetails,
  pcInOutPermitttachmentKeys,
  STATUS_IDS,
  USER_ROLES_IDS,
} from "../../../constants/common";
import useAuthStore from "../../../store/Auth";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { createPCInOutPermitSchema } from "../../../schemas/pcInOutPermitSchema";
import { formattedDateTime } from "../../../lib/functions";
import { useLocation } from "react-router-dom";
import { useGetPCPermitById } from "../../../hooks/permit/query";
import Loader from "../../../components/loader";
import { APP_ROUTES } from "../../../routes";
import { cn } from "../../../utils/cn";
import PCInOutPermitAttachmentsCard from "../../../components/card/PCInOutPermitAttachmentsCard";
import ActionButtonGroup from "./ActionButtonGroup";
import Acknowledgement from "./Acknowledgement";
import Header from "../Header";

const PcInOutPermit = () => {
  const { t, i18n } = useTranslation();

  const { user } = useAuthStore();

  const [isAccompanyPledge, setIsAccompanyPledge] = useState(false);

  const [isAttachments, setIsAttachments] = useState({
    haveEmployeeId: 0,
  });

  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  const key = searchParams.get("ID");

  const { data: pcPermit, isPending: isPCPermitLoading } = useGetPCPermitById({
    encryptedId: key || "",
  });

  const {
    control,
    formState: { errors },
    clearErrors,
    handleSubmit,
    watch,
    setValue,
  } = useFormWithSchema(createPCInOutPermitSchema(isAttachments), {
    reValidateMode: "onChange",
    defaultValues: {
      employeeName: user?.user_id?.toString(),
      permitStartDate:
        new Date() > new Date(new Date().setHours(7, 0, 0, 0))
          ? formattedDateTime(new Date())
          : formattedDateTime(new Date(new Date().setHours(7, 0, 0, 0))),
      permitEndDate: formattedDateTime(
        new Date(
          (new Date() > new Date(new Date().setHours(7, 0, 0, 0))
            ? new Date()
            : new Date(new Date().setHours(7, 0, 0, 0))
          ).getTime() +
          60 * 60 * 1000
        )
      ),
    },
  });

  const permitStartDate = new Date(watch("permitStartDate") || "");

  const uploadRefs = useRef<
    Record<string, MutableRefObject<HTMLInputElement | null>>
  >(
    pcInOutPermitttachmentKeys.reduce((acc, key) => {
      acc[key] = createRef<HTMLInputElement>();
      return acc;
    }, {} as Record<string, MutableRefObject<HTMLInputElement | null>>)
  );

  const [fileNames, setFileNames] = useState<Record<string, string>>(
    pcInOutPermitttachmentKeys.reduce((acc, key) => ({ ...acc, [key]: "" }), {})
  );

  const setFileName = (key: string, value: React.SetStateAction<string>) => {
    setFileNames((prev) => ({
      ...prev,
      [key]: typeof value === "function" ? value(prev[key]) : value,
    }));
  };

  const isPermitApprovedByNextApprover = useMemo(
    () =>
      !!(
        location.pathname === APP_ROUTES.UPDATE_PC_IN_OUT_PERMIT &&
        pcPermit?.n_CurrentApproverOrderNo &&
        pcPermit?.n_CurrentApproverOrderNo > 1
      ),
    [location.pathname, pcPermit?.n_CurrentApproverOrderNo]
  );

  const isFieldsDisabled = useMemo(
    () =>
      (user?.n_RoleID !== USER_ROLES_IDS.STAFF &&
        user?.n_RoleID !== USER_ROLES_IDS.ADMIN) ||
      (pcPermit && pcPermit?.n_ApprovalStatus !== STATUS_IDS.PENDING) ||
      (user?.n_RoleID === USER_ROLES_IDS.STAFF &&
        isPermitApprovedByNextApprover) ||
      (user?.n_RoleID === USER_ROLES_IDS.ADMIN &&
        location.pathname === APP_ROUTES.APPROVE_PC_IN_OUT_PERMIT),
    [
      isPermitApprovedByNextApprover,
      location.pathname,
      pcPermit,
      user?.n_RoleID,
    ]
  );

  const [permitDetails, setPermitDetails] = useState<PCInOutPermitRequestDetails>();

  useEffect(() => {
    if (!pcPermit) {
      setIsAccompanyPledge(false);

      setPermitDetails(undefined);

      setValue("deviceType", "");
      setValue("serialNumber", "");
      setValue("building", "");
      setValue(
        "permitStartDate",
        new Date() > new Date(new Date().setHours(7, 0, 0, 0))
          ? formattedDateTime(new Date())
          : formattedDateTime(new Date(new Date().setHours(7, 0, 0, 0)))
      );
      setValue(
        "permitEndDate",
        formattedDateTime(
          new Date(
            (new Date() > new Date(new Date().setHours(7, 0, 0, 0))
              ? new Date()
              : new Date(new Date().setHours(7, 0, 0, 0))
            ).getTime() +
            60 * 60 * 1000
          )
        )
      );

      setIsAttachments({
        haveEmployeeId: 0,
      });
      return;
    }

    const permitData = {
      id: pcPermit.n_PermitLaptopID,
      deviceType: pcPermit.s_LaptopType,
      serialNumber: pcPermit.s_LaptopSerialNumber,
      permitStartDate: pcPermit.dt_PermitStartDate,
      permitEndDate: pcPermit.dt_PermitEndDate,
      building: pcPermit.s_Building,
      employeeName: pcPermit.n_UserID.toString(),
    };

    setPermitDetails(permitData);

    setValue("employeeName", permitData.employeeName);
    setValue("deviceType", permitData.deviceType);
    setValue("serialNumber", permitData.serialNumber);
    setValue("permitStartDate", permitData?.permitStartDate);
    setValue("permitEndDate", permitData?.permitEndDate);
    setValue("building", permitData.building);

    setIsAttachments({
      haveEmployeeId: pcPermit.haveUserCardFile ? 1 : 0,
    });
  }, [pcPermit, setValue]);

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-5">
        <Header title={t("pcInOutPermit")} data={pcPermit} />

        {key && isPCPermitLoading ? (
          <div className="flex justify-center">
            <Loader />
          </div>
        ) : (
          <>
            <PCInOutPermitDetailsCard
              control={control}
              errors={errors}
              clearErrors={clearErrors}
              setValue={setValue}
              permitStartDate={permitStartDate}
              isFieldsDisabled={isFieldsDisabled}
            />

            <PCInOutPermitAttachmentsCard
              control={control}
              errors={errors}
              isAttachments={isAttachments}
              uploadRefs={uploadRefs.current}
              fileNames={fileNames}
              setFileName={setFileName}
              permitDetails={permitDetails}
              isFieldsDisabled={isFieldsDisabled}
            />

            <Acknowledgement
              pcPermit={pcPermit}
              isAccompanyPledge={isAccompanyPledge}
              setIsAccompanyPledge={setIsAccompanyPledge}
              isPermitApprovedByNextApprover={isPermitApprovedByNextApprover}
              isFieldsDisabled={isFieldsDisabled}
            />

            <ActionButtonGroup
              pcPermit={pcPermit}
              handleSubmit={handleSubmit}
              isAccompanyPledge={isAccompanyPledge}
              isPermitApprovedByNextApprover={isPermitApprovedByNextApprover}
              isFieldsDisabled={isFieldsDisabled}
            />
          </>
        )}
      </div>
    </>
  );
};

export default PcInOutPermit;
